import type { OnInit } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import type { CustomButtonEventPayload } from '@components/sp-custom-button';
import type { ButtonAdditionalEffectsSettings } from '@components/sp-button-additional-effects-settings/type/button-additional-effects-settings.type';
import type { ButtonIcon } from '@components/sp-button-icon/type/button-icon.type';
import type { ShadowSettings } from '@common/types/shadow-settings.type';
import type { ESize } from '@common/enums';

import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    HostListener,
    Output,
    inject,
    input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormField } from '@angular/forms/signals';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { EButtonStyleType, EButtonWidthType } from '@common/enums';
import { CSS_RESTRICTIONS } from '@common/constants';
import { EPixelTypes } from '@common/types/analytics-setings.type';
import { isEmptyButtonLabel } from '@common/helpers/forms';

import type { EduLoginButtonItem } from '@sites/entity-core';
import type { EduLoginButtonForm } from '@sites/entity-signal-form';

import {
    EDU_LOGIN_BUTTON_LINK,
    EDU_LOGIN_BUTTON_LINK_LABEL,
} from '@web/widgets/edu-login-button/constants/edu-login-button-widget.const';
import { analyticsSettingsFormGroup } from '@web/shared/components/web-analytics-settings/web-analytics-settings.form';
import { WebCustomButtonComponent } from '@web/shared/components/web-custom-button/web-custom-button.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebAnalyticsSettingsModule } from '@web/shared/components/web-analytics-settings';
import { SpButtonIconComponent } from '@components/sp-button-icon/sp-button-icon.component';
import { SpShadowSettingsComponent } from '@components/sp-shadow-settings/sp-shadow-settings.component';

/**
 * Per-button editor for the `edu-login-button` widget — a fork of the button
 * settings panel. The single behavioural difference: the link is fixed to
 * {@link EDU_LOGIN_BUTTON_LINK} and non-editable, so the link-type dropdown is
 * gone and the URL field is rendered as a locked, pre-filled input (no
 * `[formField]` binding — the value can never change).
 */
@Component({
    selector: 'web-edu-login-button-settings',
    templateUrl: './edu-login-button-settings.component.html',
    styleUrls: ['./edu-login-button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        WebCustomButtonComponent,
        WebAnalyticsSettingsModule,
        SpButtonIconComponent,
        SpShadowSettingsComponent,
        FormField,
    ],
})
export class EduLoginButtonSettingsComponent implements OnInit {
    public readonly form = input.required<EduLoginButtonForm>();
    public readonly index = input.required<number>();
    public readonly isSpecificWidget = input<boolean>(false);
    public readonly isZeroWidget = input<boolean>(false);

    @Output() public saveEvent: EventEmitter<void> = new EventEmitter();
    @Output() public cancelEvent: EventEmitter<void> = new EventEmitter();

    private readonly destroyRef = inject(DestroyRef);

    public readonly EButtonStyleType = EButtonStyleType;
    public readonly EButtonWidthType = EButtonWidthType;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly CSS_RESTRICTIONS = CSS_RESTRICTIONS;
    public readonly EDU_LOGIN_BUTTON_LINK = EDU_LOGIN_BUTTON_LINK;
    public readonly EDU_LOGIN_BUTTON_LINK_LABEL = EDU_LOGIN_BUTTON_LINK_LABEL;
    protected readonly EPixelTypes = EPixelTypes;

    public isShowAnalyticsSettings: boolean = false;

    /**
     * Reactive-forms bridge for `web-analytics-settings` — seeded from the
     * signal form in `ngOnInit`, synced back on every change.
     */
    public readonly analyticsForm = analyticsSettingsFormGroup();

    public ngOnInit(): void {
        const settings = this.itemValue?.analyticsSettings;
        if (settings) {
            this.analyticsForm.patchValue(settings as any, { emitEvent: false });
        }

        this.analyticsForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.patch({
                analyticsSettings:
                    this.analyticsForm.getRawValue() as unknown as EduLoginButtonItem['analyticsSettings'],
            });
        });
    }

    /** FieldTree of the edited button — `undefined` until the form holds the index. */
    public get itemField(): FieldTree<EduLoginButtonItem> | undefined {
        return this.form().buttonAt(this.index());
    }

    /** Value snapshot of the edited button. */
    public get itemValue(): EduLoginButtonItem | undefined {
        return this.itemField?.().value();
    }

    /**
     * Button text is required: blank text renders an unusable button, so the
     * field is highlighted and both Save and the Enter shortcut are blocked.
     */
    public get saveDisablePredicate(): boolean {
        return isEmptyButtonLabel(this.itemValue?.label);
    }

    private patch(patch: Partial<EduLoginButtonItem>): void {
        this.form().updateButton(this.index(), patch);
    }

    // --- Simple fields ---

    public setSize(value: string): void {
        this.patch({ size: value as ESize });
    }

    public setStyleType(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as EduLoginButtonItem['styleType'];
        this.patch({ styleType: value });
    }

    public setWidthType(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as EduLoginButtonItem['widthType'];
        this.patch({ widthType: value });
    }

    public setWidthValue(value: number): void {
        this.patch({ widthValue: value });
    }

    public toggleTargetBlank(value: boolean): void {
        this.patch({ isTargetBlank: value });
    }

    public setNewElementId(newId: string): void {
        this.patch({ elementId: newId });
    }

    // --- Style groups ---

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        const patch: Partial<EduLoginButtonItem> = {
            buttonBase: changes.base as unknown as EduLoginButtonItem['buttonBase'],
            buttonHover: changes.hover as unknown as EduLoginButtonItem['buttonHover'],
        };

        if (changes?.isIndependentCorners !== undefined) {
            patch.isIndependentCorners = changes.isIndependentCorners;
        }
        if (changes?.radiusSettings) {
            patch.advancedRadius = changes.radiusSettings as EduLoginButtonItem['advancedRadius'];
        }
        if (changes?.advancedBorderSettings !== undefined) {
            patch.advancedBorderSettings =
                changes.advancedBorderSettings as unknown as EduLoginButtonItem['advancedBorderSettings'];
        }

        this.patch(patch);
    }

    public buttonIconChangeHandler(changes: ButtonIcon): void {
        this.patch({ buttonIcon: changes as unknown as EduLoginButtonItem['buttonIcon'] });
    }

    public additionalEffectsChangeHandler(changes: ButtonAdditionalEffectsSettings): void {
        this.patch({ additionalEffects: changes as unknown as EduLoginButtonItem['additionalEffects'] });
    }

    public setShadow(value: boolean): void {
        this.patch({ isShadow: value });
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.patch({ shadowSettings: settings });
    }

    // --- Analytics ---

    public setSettingsView(): void {
        this.isShowAnalyticsSettings = !this.isShowAnalyticsSettings;
    }

    public get isEnabledAnalytic(): boolean {
        const settings = this.itemValue?.analyticsSettings;
        return Boolean(settings?.googleAnalytic || settings?.pixelAnalytic);
    }

    // --- Save / cancel ---

    public onSave(): void {
        if (this.saveDisablePredicate) {
            return;
        }

        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }
}
